<script setup lang="ts" generic="T extends Record<string, any>">
import type { FormSchema } from '#layers/block-engine/public';
import type { CompositePrimaryKey } from '#layers/data-model/public';
import type { RStoreCollectionApi } from '#layers/forms/composables/use-rstore-form-object';
import type { FormProps } from '#layers/ui/public';
import { pickNonSpecialProps } from '@rstore/shared';
import { useDebounceFn } from '@vueuse/core';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { LazyFormSchemaFields } from '#layers/block-engine/lazy';
import { createMutationAdapter, getDefaultValuesFromFormSchema } from '#layers/block-engine/public';
import { useFormSubmitHandler } from '#layers/forms/composables/use-form-submit-handler';
import { useOptimisticLayer } from '#layers/forms/composables/use-optimistic-layer';
import { useRStoreFormObject } from '#layers/forms/composables/use-rstore-form-object';
import { useSystemEntityStore } from '#layers/runtime/public';
import { ErrorAlert, useEditGuard, useToastResultHandling } from '#layers/ui/public';

const {
	layout = 'side-by-side',
	collection: collectionProp,
	itemKey,
	schema,
	resetOnSuccess,
	autoSave,
	optimistic,
	disabled,
	layoutSplit,
} = defineProps<Props>();

const emit = defineEmits<{
	success: [item: T];
	error: [error: Error];
}>();

const AUTO_SAVE_INTERVAL_MS = 500;
const isFirstLoad = ref(true);

interface Props extends Pick<FormProps, 'layout' | 'layoutSplit'> {
	/** FormSchema definition (static array or function) */
	schema: FormSchema<T>;

	/** RStore collection object (e.g., store.Workspace) */
	collection: RStoreCollectionApi;

	/** Primary key for update mode, null/undefined for create */
	itemKey?: CompositePrimaryKey | null;

	/** Reset form after successful create */
	resetOnSuccess?: boolean;

	/** Disable entire form */
	disabled?: boolean;

	/**
	 * Enable auto-save functionality
	 *
	 * Automatically saves form data debounced after `AUTO_SAVE_INTERVAL_MS` milliseconds.
	 */
	autoSave?: boolean;

	/** Apply form changes to the rstore cache immediately, before the debounced save completes. Requires autoSave. */
	optimistic?: boolean;
}

const forwarded = computed(() => ({ layout, layoutSplit }));

const { formObject, isCreate, isLoading, initError, hasChanges } = useRStoreFormObject<T>(
	() => collectionProp,
	() => itemKey,
	{
		defaultValues: () => getDefaultValuesFromFormSchema({ schema: schema as FormSchema }) as Partial<T>,
		resetOnSuccess: () => resetOnSuccess,
		onError: (error) => {
			emit('error', error);
			return false;
		},
	},
);

// The `submitted` flag prevents false "unsaved changes" warnings in this edge case:
// - Create mode with resetOnSuccess: false
// - Form submits successfully but doesn't reset
// - Brief window where $hasChanges() may still return true
const submitted = ref(false);

function useOptimisticFormLayer() {
	if (!autoSave || !optimistic) return null;

	const store = useSystemEntityStore();
	// Resolve collection name by matching the collection API reference against the store's cached proxies.
	// RStore returns the same cached proxy instance for each collection name access.
	const collectionName = computed(() => {
		for (const collection of store.$collections) {
			if ((store as any)[collection.name] === collectionProp) return collection.name;
		}

		// eslint-disable-next-line unicorn/no-useless-undefined
		return undefined;
	});

	if (!collectionName.value) return null;

	return useOptimisticLayer({
		collectionName,
		// Derive key from the loaded form object (full item) rather than props.itemKey (partial key object),
		// because custom getKey resolvers (e.g. MonospaceWorkspace: apiName ?? id) need the full item
		itemKey: computed(() => formObject.value ? collectionProp.getKey(formObject.value) ?? undefined : undefined),
	});
}

const optimisticLayer = useOptimisticFormLayer();

const { onSubmit, submitDisabled, submitError } = useFormSubmitHandler<T>(formObject, {
	isCreate,
	onSuccess: (item) => {
		submitted.value = true;
		// Only remove the optimistic layer if no new edits arrived during the save
		if (!hasChanges.value) optimisticLayer?.commit();
		emit('success', item);
	},
	onError: (error) => {
		if (!hasChanges.value) optimisticLayer?.rollback();
		emit('error', error);
	},
	onBeforeSubmit: () => {
		submitted.value = false;
	},
});

useEditGuard(() => hasChanges.value && !submitted.value);

const { t } = useI18n();
const { onError } = useToastResultHandling({
	error: {
		title: t('common.save_failed'),
	},
});

async function performAutoSave() {
	if (!autoSave || !formObject.value || isCreate.value) return;
	if (!hasChanges.value || formObject.value.$loading) return;

	try {
		await onSubmit();
	}
	catch (error) {
		onError(error);
	}
}

const autoSaveDebounced = useDebounceFn(performAutoSave, AUTO_SAVE_INTERVAL_MS);

if (autoSave) {
	watch(
		() => formObject.value,
		() => {
			if (isFirstLoad.value) {
				isFirstLoad.value = false;
				return;
			}

			if (autoSave && hasChanges.value) {
				if (optimisticLayer && formObject.value) {
					optimisticLayer.update(pickNonSpecialProps(formObject.value));
				}

				autoSaveDebounced();
			}
		},
		{ deep: true },
	);

	onBeforeUnmount(() => {
		// make sure we save the form when navigating away
		performAutoSave();
	});
}

const formSchemaAdapter = createMutationAdapter(formObject);

const isDisabled = computed(() =>
	disabled || isLoading.value || !!initError.value,
);

const displayError = computed(() => initError.value || submitError.value);

defineExpose({
	formObject,
	hasChanges: () => hasChanges.value,
	reset: () => formObject.value?.$reset?.(),
	submit: onSubmit,
});
</script>

<template>
	<div class="flex flex-col gap-4">
		<ErrorAlert
			v-if="displayError"
			:error="displayError"
		/>

		<DForm
			v-if="formObject"
			v-bind="forwarded"
			:state="formObject"
			:disabled="isDisabled"
			auto-focus-first-input
			@submit="onSubmit"
		>
			<LazyFormSchemaFields
				:schema
				:adapter="formSchemaAdapter"
				:context="{ isCreate, collection: collectionProp }"
			/>

			<template #content-bottom>
				<slot
					name="actions"
					:is-create
					:submit-disabled="submitDisabled || isDisabled"
					:loading="formObject.$loading"
					:has-changes
				>
					<div v-if="!autoSave" class="flex justify-end gap-2 pt-6">
						<DButton
							type="submit"
							:label="isCreate ? $t('common.create') : $t('common.save')"
							:loading="formObject.$loading"
							:disabled="submitDisabled || isDisabled"
						/>
					</div>
				</slot>
			</template>
		</DForm>

		<div
			v-else
			class="p-4 text-center text-dimmed"
		>
			<p>{{ $t('common.no_data') }}</p>
		</div>
	</div>
</template>
